<template>
    <div>
        <div class="page-header pr-0 restaurant-page-header">
            <h2><a href="#"><i class="fas fa-receipt"></i></a></h2>
            <ol class="breadcrumbs">
                <li class="active"><span>Restaurante V2</span></li>
                <li><span>Pedido por mesa</span></li>
            </ol>
        </div>

        <div class="restaurant-pos" v-if="order">
            <div class="restaurant-pos-main">
                <div class="restaurant-table-summary">
                    <div>
                        <h4>
                            {{ order.room ? order.room.name : '' }} -
                            {{ order.dining_table ? order.dining_table.name : '' }}
                        </h4>
                    </div>

                    <p>
                        Pedido #{{ order.id }} | Personas: {{ order.people_quantity }} | Estado: {{ order.status }}
                    </p>
                </div>

                <template v-if="!isClosed">

                    <div class="restaurant-category-tabs">
                        <button :class="{ active: selectedCategory === 'all' }" @click="selectedCategory = 'all'">
                            Todos
                        </button>

                        <button v-for="category in categories" :key="category"
                            :class="{ active: selectedCategory === category }" @click="selectedCategory = category">
                            {{ category }}
                        </button>
                    </div>

                    <div class="restaurant-products-grid">
                        <div v-for="item in filteredItems" :key="item.card_key" class="restaurant-product-card" :class="{
                            'without-image': !showProductImages,
                            'has-presentation': !!item.item_unit_type_id
                        }" @click="quickAddItem(item)">
                            <div v-if="showProductImages" class="restaurant-product-image">
                                <img v-if="item.image_url" :src="item.image_url" :alt="item.description"
                                    @error="item.image_url = null">

                                <div v-else class="restaurant-product-placeholder">
                                    <i class="fas fa-utensils"></i>
                                </div>
                            </div>

                            <div class="restaurant-product-body">
                                <h5>{{ item.description }}</h5>

                                <div v-if="item.presentation_description" class="restaurant-product-presentation">
                                    {{ item.presentation_description }}
                                </div>

                                <div v-else class="restaurant-product-presentation empty">
                                    Producto base
                                </div>

                                <div class="restaurant-product-footer">
                                    <strong>
                                        S/
                                        {{ Number(
                                            item.sale_unit_price || 0
                                        ).toFixed(2) }}
                                    </strong>

                                    <div class="restaurant-product-actions">
                                        <button v-if="canAddItems" class="btn-add-product"
                                            @click.stop="quickAddItem(item)">
                                            <i class="fas fa-plus"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="filteredItems.length === 0" class="restaurant-empty-products">
                            No hay productos para esta categoría
                        </div>
                    </div>

                </template>

                <div v-else class="restaurant-closed-state">
                    <i class="fas fa-check-circle"></i>
                    <h3>Pedido cerrado</h3>
                    <p>La mesa ya fue liberada y el comprobante fue generado correctamente.</p>

                    <button class="btn btn-primary" @click="goToTablesMap">
                        Volver al mapa de mesas
                    </button>
                </div>
            </div>

            <div class="restaurant-order-panel">
                <div class="restaurant-order-card">
                    <div class="restaurant-order-items">
                        <div v-if="pendingOrderItems.length" class="restaurant-order-section-title pending">
                            Pendiente para comanda
                        </div>

                        <div v-for="row in pendingOrderItems" :key="row.group_key"
                            class="restaurant-order-row pending-row">
                            <div class="restaurant-order-row-top">
                                <div class="restaurant-order-product-title">
                                    <strong>{{ orderItemDescription(row) }}</strong>
                                    <el-tag size="mini" type="warning">Pendiente</el-tag>
                                </div>

                                <div class="restaurant-row-side">
                                    <button v-if="!isClosed && canAddItems && row.status === 'pending'"
                                        class="btn-note-product" title="Agregar nota"
                                        @click.prevent="openOrderItemNoteDialog(row)">
                                        <i class="fas fa-sticky-note"></i>
                                    </button>

                                    <strong class="restaurant-row-total">
                                        S/ {{ Number(row.total).toFixed(2) }}
                                    </strong>
                                </div>
                            </div>

                            <div class="restaurant-order-row-bottom">
                                <div class="restaurant-row-detail-wrap">
                                    <button v-if="canOpenOrderItemDetail(row)" type="button"
                                        class="restaurant-row-detail restaurant-row-detail-button"
                                        @click.stop="openOrderItemDetail(row)">
                                        {{ Number(row.quantity).toFixed(0) }} und. x S/ {{
                                            Number(row.unit_price).toFixed(2) }}
                                    </button>

                                    <span v-else class="restaurant-row-detail">
                                        {{ Number(row.quantity).toFixed(0) }} und. x S/ {{
                                            Number(row.unit_price).toFixed(2) }}
                                    </span>

                                    <!-- <span v-if="row.note" class="restaurant-note-bubble">
                                        <i class="fas fa-sticky-note"></i>
                                        {{ row.note }}
                                    </span> -->
                                </div>

                                <div class="restaurant-row-actions" v-if="!isClosed && canDeleteItems">
                                    <button @click="decreaseItem(row)">-</button>
                                    <span>{{ Number(row.quantity).toFixed(0) }}</span>
                                    <button @click="increaseItem(row)">+</button>

                                    <button class="delete" @click.prevent="requestDeleteItem(row)">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div v-if="sentOrderItems.length" class="restaurant-order-section-title sent">
                            Ya enviado a cocina/bar
                        </div>

                        <div v-for="row in sentOrderItems" :key="row.group_key" class="restaurant-order-row sent-row">
                            <div class="restaurant-order-row-top">
                                <div class="restaurant-order-product-title">
                                    <strong>{{ orderItemDescription(row) }}</strong>
                                    <el-tag size="mini" :type="orderItemStatusType(row.status)">
                                        {{ orderItemStatusText(row.status) }}
                                    </el-tag>
                                </div>

                                <strong class="restaurant-row-total">
                                    S/ {{ Number(row.total).toFixed(2) }}
                                </strong>
                            </div>

                            <div class="restaurant-order-row-bottom">
                                <div class="restaurant-row-detail-wrap">
                                    <button v-if="canOpenOrderItemDetail(row)" type="button"
                                        class="restaurant-row-detail restaurant-row-detail-button"
                                        @click.stop="openOrderItemDetail(row)">
                                        {{ Number(row.quantity).toFixed(0) }} und. x S/ {{
                                            Number(row.unit_price).toFixed(2) }}
                                    </button>

                                    <span v-else class="restaurant-row-detail">
                                        {{ Number(row.quantity).toFixed(0) }} und. x S/ {{
                                            Number(row.unit_price).toFixed(2) }}
                                    </span>

                                    <!-- <span v-if="row.note" class="restaurant-note-bubble">
                                        <i class="fas fa-sticky-note"></i>
                                        {{ row.note }}
                                    </span> -->
                                </div>
                            </div>
                        </div>

                        <div v-if="visibleOrderItems.length === 0" class="restaurant-empty-order">
                            Aún no agregas productos
                        </div>
                    </div>

                    <div class="restaurant-order-footer">
                        <div class="restaurant-ready-alert" v-if="isReadyToServe && !isClosed">
                            <i class="fas fa-concierge-bell"></i>
                            Pedido listo para servir
                        </div>

                        <div class="restaurant-total-line">
                            <span>Total</span>
                            <strong>S/ {{ order.total | toDecimals }}</strong>
                        </div>

                        <div v-if="canCharge && !canChargeByWorkflow" class="restaurant-preaccount-required-alert">
                            <i class="fas fa-info-circle"></i>
                            Primero imprime la pre cuenta para habilitar el cobro.
                        </div>

                        <div class="restaurant-closed-alert" v-if="isClosed">
                            Pedido cerrado. La mesa ya fue liberada.
                        </div>

                        <div class="restaurant-order-buttons compact" v-if="!isClosed">
                            <button v-if="canSendCommand" class="btn btn-success" @click="sendKitchen">
                                <i class="fas fa-receipt"></i>
                                <span>Comanda</span>
                            </button>

                            <button v-if="showPreAccountButton" class="btn btn-warning" @click="printPreAccount">
                                <i class="fas fa-file-invoice"></i>
                                <span>{{ order.status === 'ready_to_pay' ? 'Reimprimir pre cuenta' : 'Pre cuenta'
                                    }}</span>
                            </button>

                            <button v-if="canCharge" class="btn"
                                :class="canChargeByWorkflow ? 'btn-primary' : 'btn-default restaurant-disabled-action'"
                                @click="openCheckout">
                                <i class="fas fa-cash-register"></i>
                                <span>{{ canChargeByWorkflow ? 'Cobrar' : 'Cobro bloqueado' }}</span>
                            </button>

                            <button class="btn btn-default" @click="openTableOptions">
                                <i class="fas fa-chair"></i>
                                <span>Mesa</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog title="Código de autorización" :visible.sync="showAuthorizationDialog" width="420px"
            :close-on-click-modal="false">

            <div class="form-group">
                <label>
                    {{ authorizationForm.action === 'cancel_order'
                        ? 'Ingrese el código para cancelar/liberar la mesa'
                        : 'Ingrese el código para eliminar el producto'
                    }}
                </label>
                <el-input v-model="authorizationForm.code" show-password placeholder="Código"
                    @keyup.enter.native="confirmAuthorizedDelete">
                </el-input>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button @click="closeAuthorizationDialog">
                    Cancelar
                </el-button>

                <el-button type="primary" @click="confirmAuthorizedDelete">
                    Confirmar
                </el-button>
            </span>
        </el-dialog>

        <el-dialog title="Nota del producto" :visible.sync="showNoteProductDialog" width="480px">
            <div v-if="noteOrderItemSelected">
                <div class="restaurant-note-product-header">
                    <strong>{{ noteOrderItemSelected.item ? noteOrderItemSelected.item.description : '' }}</strong>
                    <span>S/ {{ Number(noteOrderItemSelected.total || 0).toFixed(2) }}</span>
                </div>

                <div class="form-group">
                    <label>Notas rápidas</label>
                    <div class="restaurant-note-tags">
                        <button v-for="tag in quickNoteTags" :key="tag" type="button"
                            :class="{ active: noteProductForm.tags.includes(tag) }" @click="toggleNoteTag(tag)">
                            {{ tag }}
                        </button>
                    </div>
                </div>

                <div class="form-group">
                    <label>Nota adicional</label>
                    <el-input type="textarea" rows="3" v-model="noteProductForm.custom_note"
                        placeholder="Ejemplo: sin ají, bien cocido, para llevar">
                    </el-input>
                </div>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button @click="closeNoteProductDialog">
                    Cancelar
                </el-button>

                <el-button type="primary" @click="submitOrderItemNote">
                    Guardar nota
                </el-button>
            </span>
        </el-dialog>

        <el-dialog :visible.sync="showOrderItemDetailDialog" width="620px"
            custom-class="restaurant-order-item-detail-dialog">

            <span slot="title">
                <div class="restaurant-detail-title">
                    <span>Detalle por orden de pedido:</span>
                    <strong>
                        {{ orderItemDetailSelected && orderItemDetailSelected.item ?
                            orderItemDetailSelected.item.description :
                            '' }}
                    </strong>
                </div>
            </span>

            <div v-if="orderItemDetailSelected">
                <div v-for="detail in orderItemDetails" :key="detail.id" class="restaurant-order-item-detail-row">

                    <div class="restaurant-order-item-detail-info">
                        <div class="restaurant-order-item-detail-date">
                            <i class="far fa-calendar-alt"></i>
                            {{ formatOrderItemDate(detail.created_at) }}
                        </div>

                        <div class="restaurant-order-item-detail-meta">
                            <el-tag size="mini" :type="orderItemStatusType(detail.status)">
                                {{ orderItemStatusText(detail.status) }}
                            </el-tag>

                            <span>
                                :: {{ Number(detail.quantity).toFixed(0) }} Unidad(es)
                            </span>
                        </div>

                        <div v-if="detail.note" class="restaurant-order-item-detail-note">
                            <span>Notas:</span>
                            <strong>{{ detail.note }}</strong>
                        </div>
                    </div>

                    <button v-if="!isClosed && canDeleteItems" type="button" class="restaurant-order-item-detail-delete"
                        @click="requestDeleteItem(detail)">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>

                <div v-if="orderItemDetails.length === 0" class="restaurant-empty-order">
                    No hay detalle para este producto.
                </div>
            </div>
        </el-dialog>

        <checkout :showDialog.sync="showCheckout" :order="order" @generated="afterGenerated">
        </checkout>

        <el-dialog :visible.sync="showGeneratedOptions" :close-on-click-modal="false"
            :close-on-press-escape="false" :show-close="false" append-to-body width="30%"
            custom-class="pos-payment-dialog" @open="loadGeneratedRecord">

            <span slot="title">
                <strong>Comprobante: {{ generatedOptionsForm.number }}</strong>
            </span>

            <div>
                <div class="row">
                    <div class="col text-center font-weight-bold mt-3">
                        <button type="button" class="btn btn-lg btn-info waves-effect waves-light"
                            @click="openGeneratedPrint('a4')">
                            <i class="fa fa-file-alt"></i>
                        </button>
                        <p>A4</p>
                    </div>

                    <div v-if="showGeneratedTicket80" class="col text-center font-weight-bold mt-3">
                        <button type="button" class="btn btn-lg btn-info waves-effect waves-light"
                            @click="openGeneratedPrint('ticket')">
                            <i class="fa fa-receipt"></i>
                        </button>
                        <p>80MM</p>
                    </div>

                    <div v-if="showGeneratedTicket58" class="col text-center font-weight-bold mt-3">
                        <button type="button" class="btn btn-lg btn-info waves-effect waves-light"
                            @click="openGeneratedPrint('ticket_58')">
                            <i class="fa fa-receipt"></i>
                        </button>
                        <p>58MM</p>
                    </div>

                    <div v-if="showGeneratedTicket50" class="col text-center font-weight-bold mt-3">
                        <button type="button" class="btn btn-lg btn-info waves-effect waves-light"
                            @click="openGeneratedPrint('ticket_50')">
                            <i class="fa fa-receipt"></i>
                        </button>
                        <p>50MM</p>
                    </div>

                    <div class="col text-center font-weight-bold mt-3">
                        <button type="button" class="btn btn-lg btn-info waves-effect waves-light"
                            @click="openGeneratedPrint('a5')">
                            <i class="fa fa-file-alt"></i>
                        </button>
                        <p>A5</p>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-md-12">
                        <el-input v-model="generatedOptionsForm.customer_email">
                            <el-button slot="append" icon="el-icon-message" @click="sendGeneratedEmail">
                                Enviar
                            </el-button>
                        </el-input>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-md-12">
                        <el-input v-model="generatedOptionsForm.customer_telephone">
                            <template slot="prepend">+51</template>
                            <el-button slot="append" @click="sendGeneratedWhatsapp">
                                Enviar <i class="fab fa-whatsapp"></i>
                            </el-button>
                        </el-input>
                    </div>
                </div>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button @click="closeGeneratedOptions">Ir a mesas</el-button>
            </span>
        </el-dialog>

        <el-dialog title="Opciones de mesa" :visible.sync="showTableOptions" width="480px">
            <div class="form-group">
                <label>Mover a mesa disponible</label>
                <el-select v-model="moveTableForm.restaurant_dining_table_id" filterable class="w-100">
                    <el-option v-for="table in availableTables" :key="table.id" :label="table.description"
                        :value="table.id">
                    </el-option>
                </el-select>
            </div>

            <div class="d-flex justify-content-between mt-4">
                <button class="btn btn-danger" @click="requestCancelOrder">
                    <i class="fas fa-ban"></i> Cancelar / liberar mesa
                </button>

                <button class="btn btn-primary" :disabled="!moveTableForm.restaurant_dining_table_id"
                    @click="moveTable">
                    <i class="fas fa-exchange-alt"></i> Mover mesa
                </button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import Checkout from './checkout.vue'
import { mapState } from 'vuex/dist/vuex.mjs'

export default {
    components: {
        Checkout,
    },
    props: ['orderId'],
    data() {
        return {
            resource: 'restaurant/v2/orders',
            order: null,
            items: [],
            selectedCategory: 'all',
            form: {
                item_id: null,
                item_unit_type_id: null,
                quantity: 1,
                note: null,
            },
            showCheckout: false,
            showDocumentOptions: false,
            showSaleNoteOptions: false,
            generatedRecordId: null,
            optionsWasOpened: false,
            showGeneratedOptions: false,
            generatedRecordId: null,
            generatedResource: null,
            generatedOptionsForm: {
                id: null,
                number: null,
                external_id: null,
                customer_email: null,
                customer_telephone: null,
                print_a4: null,
                print_a5: null,
                print_ticket: null,
                print_ticket_50: null,
                print_ticket_58: null,
            },
            showTableOptions: false,
            availableTables: [],
            moveTableForm: {
                restaurant_dining_table_id: null,
            },
            restaurant_permissions: {},
            restaurant_role_code: null,
            restaurant_configuration: {
                restaurant_require_authorization_code: false,
                restaurant_show_product_images: true,
                restaurant_allow_reservations: true,
                restaurant_show_elapsed_time: true,
                restaurant_use_pre_account_color: true,
                restaurant_allow_charge_without_pre_account: true,
            },
            showAuthorizationDialog: false,
            authorizationForm: {
                code: null,
                item_id: null,
                action: null,
            },
            showNoteProductDialog: false,
            noteProductSelected: null,
            noteOrderItemSelected: null,
            showOrderItemDetailDialog: false,
            orderItemDetailSelected: null,
            quickNoteTags: [
                'sin ají',
                'con ají',
                'bien cocido',
                'sin cebolla',
                'para llevar',
                'sin sal',
            ],
            noteProductForm: {
                quantity: 1,
                tags: [],
                custom_note: null,
            },
        }
    },
    computed: {
        ...mapState([
            'config',
        ]),
        showGeneratedTicket80() {
            return !!(this.config && this.config.show_ticket_80)
        },
        showGeneratedTicket58() {
            return !!(this.config && this.config.show_ticket_58)
        },
        showGeneratedTicket50() {
            return !!(this.config && this.config.show_ticket_50)
        },
        isClosed() {
            return this.order && ['closed', 'cancelled'].includes(this.order.status)
        },
        categories() {
            const categories = this.items.map(item => this.getItemCategory(item))
            return [...new Set(categories)].filter(category => category)
        },
        filteredItems() {
            if (this.selectedCategory === 'all') {
                return this.items
            }

            return this.items.filter(item => this.getItemCategory(item) === this.selectedCategory)
        },
        visibleOrderItems() {
            const groups = []
            const grouped = {}

                ; (this.order.items || []).forEach(row => {
                    const status = row.status || 'pending'
                    const area =
                        row.restaurant_production_area_id || 'no_area'

                    const presentation =
                        row.item_unit_type_id || 'base'

                    const note = row.note || ''

                    const unitPrice =
                        Number(row.unit_price || 0).toFixed(6)

                    const key = [
                        row.item_id,
                        presentation,
                        area,
                        status,
                        unitPrice,
                        note,
                    ].join('_')

                    if (!grouped[key]) {
                        grouped[key] = {
                            ...row,
                            group_key: `group_${key}`,
                            ids: [row.id],
                            quantity: Number(row.quantity),
                            total: Number(row.total),
                            status: status,
                            note: row.note || null,
                        }

                        groups.push(grouped[key])
                    } else {
                        grouped[key].ids.push(row.id)
                        grouped[key].quantity += Number(row.quantity)
                        grouped[key].total += Number(row.total)
                    }
                })

            return groups
        },
        pendingOrderItems() {
            return this.visibleOrderItems.filter(row => row.status === 'pending' || !row.status)
        },

        sentOrderItems() {
            return this.visibleOrderItems.filter(row => ['sent', 'ready'].includes(row.status))
        },
        orderItemDetails() {
            if (!this.orderItemDetailSelected || !this.order || !this.order.items) {
                return []
            }

            const ids = this.orderItemDetailSelected.ids || [this.orderItemDetailSelected.id]

            return this.order.items.filter(row => ids.includes(row.id))
        },
        isReadyToServe() {
            if (!this.order || !this.order.items || this.order.items.length === 0) {
                return false
            }

            return this.order.items.every(row => row.status === 'ready')
        },
        canAddItems() {
            return !!this.restaurant_permissions.can_add_items
        },
        canDeleteItems() {
            return !!this.restaurant_permissions.can_delete_items
        },
        canSendCommand() {
            return !!this.restaurant_permissions.can_send_command
        },
        canPrintPreAccount() {
            return !!this.restaurant_permissions.can_print_pre_account
        },
        canCharge() {
            return !!this.restaurant_permissions.can_charge
        },
        canChargeWithoutPreAccount() {
            return !this.restaurant_configuration
                || this.restaurant_configuration.restaurant_allow_charge_without_pre_account !== false
        },
        showPreAccountButton() {
            return this.canPrintPreAccount && !this.canChargeWithoutPreAccount
        },
        canChargeByWorkflow() {
            return this.canChargeWithoutPreAccount
                || (this.order && this.order.status === 'ready_to_pay')
        },
        mustAskAuthorizationCode() {
            return this.restaurant_role_code === 'MOZO'
                && this.restaurant_configuration
                && this.restaurant_configuration.restaurant_require_authorization_code
        },
        showProductImages() {
            return !this.restaurant_configuration
                || this.restaurant_configuration.restaurant_show_product_images !== false
        },
    },
    created() {
        this.getTables()
        this.getRecord()
    },
    watch: {
        showDocumentOptions(value) {
            if (!value && this.optionsWasOpened) {
                this.goToTablesMap()
            }
        },

        showSaleNoteOptions(value) {
            if (!value && this.optionsWasOpened) {
                this.goToTablesMap()
            }
        },
    },
    methods: {
        getTables() {
            this.$http
                .get(`/${this.resource}/tables`)
                .then(response => {
                    const products = response.data.items || []
                    const cards = []

                    products.forEach(product => {
                        const presentations = Array.isArray(
                            product.presentations
                        )
                            ? product.presentations
                            : []

                        /*
                         * Si tiene presentaciones:
                         * mostramos exclusivamente sus presentaciones.
                         */
                        if (product.has_presentations) {
                            presentations.forEach(presentation => {
                                cards.push({
                                    ...product,

                                    card_key:
                                        `item_${product.id}` +
                                        `_presentation_${presentation.id}`,

                                    item_unit_type_id:
                                        presentation.id,

                                    presentation_description:
                                        presentation.description,

                                    presentation_quantity_unit:
                                        Number(
                                            presentation.quantity_unit || 1
                                        ),

                                    sale_unit_price:
                                        Number(
                                            presentation.unit_price || 0
                                        ),

                                    selected_presentation:
                                        presentation,

                                    image_url:
                                        this.getItemImage(product),
                                })
                            })

                            return
                        }

                        /*
                         * Si no tiene presentaciones:
                         * se muestra el precio principal.
                         */
                        cards.push({
                            ...product,

                            card_key:
                                `item_${product.id}_base`,

                            item_unit_type_id: null,
                            presentation_description: null,
                            presentation_quantity_unit: 1,
                            selected_presentation: null,

                            sale_unit_price:
                                Number(product.sale_unit_price || 0),

                            image_url:
                                this.getItemImage(product),
                        })
                    })

                    this.items = cards
                })
        },
        getRecord() {
            this.$http.get(`/${this.resource}/${this.orderId}/record`).then(response => {
                this.order = response.data.data
                this.restaurant_permissions = response.data.permissions || {}
                this.restaurant_role_code = response.data.restaurant_role_code || null
                this.restaurant_configuration = response.data.restaurant_configuration || {
                    restaurant_require_authorization_code: false,
                }
            })
        },
        getItemCategory(item) {
            if (typeof item.category === 'string' && item.category) return item.category
            if (item.category && item.category.name) return item.category.name
            if (item.category && item.category.description) return item.category.description
            if (item.item_category && item.item_category.description) return item.item_category.description
            if (item.category_name) return item.category_name
            return 'General'
        },
        getItemImage(item) {
            const invalidImages = [
                null,
                '',
                'imagen-no-disponible.jpg',
                'no-image.jpg',
            ]

            if (item.image_url && !invalidImages.includes(item.image_url)) {
                return item.image_url
            }

            if (item.image && !invalidImages.includes(item.image)) {
                return `/storage/uploads/items/${item.image}`
            }

            if (item.image_medium && !invalidImages.includes(item.image_medium)) {
                return `/storage/uploads/items/${item.image_medium}`
            }

            return null
        },
        openNoteProductDialog(item) {
            if (!this.canAddItems) {
                this.$message.warning('No tienes permiso para agregar productos')
                return
            }

            if (this.isClosed) {
                this.$message.warning('El pedido ya está cerrado')
                return
            }

            this.noteProductSelected = item
            this.noteProductForm = {
                quantity: 1,
                tags: [],
                custom_note: null,
            }
            this.showNoteProductDialog = true
        },

        closeNoteProductDialog() {
            this.showNoteProductDialog = false
            this.noteProductSelected = null
            this.noteOrderItemSelected = null
        },

        toggleNoteTag(tag) {
            const index = this.noteProductForm.tags.indexOf(tag)

            if (index >= 0) {
                this.noteProductForm.tags.splice(index, 1)
            } else {
                this.noteProductForm.tags.push(tag)
            }
        },

        buildProductNote() {
            const notes = []

            this.noteProductForm.tags.forEach(tag => {
                notes.push(tag)
            })

            if (this.noteProductForm.custom_note) {
                this.noteProductForm.custom_note
                    .split(',')
                    .map(row => row.trim())
                    .filter(row => row)
                    .forEach(row => notes.push(row))
            }

            return [...new Set(notes)].join(', ')
        },

        submitNoteProduct() {
            if (!this.noteProductSelected) {
                return
            }

            this.form.item_id =
                this.noteProductSelected.id

            this.form.item_unit_type_id =
                this.noteProductSelected.item_unit_type_id || null

            this.form.quantity =
                this.noteProductForm.quantity || 1

            this.form.note =
                this.buildProductNote()

            this.addItem()
            this.closeNoteProductDialog()
        },
        quickAddItem(item) {
            if (!this.canAddItems) {
                this.$message.warning(
                    'No tienes permiso para agregar productos'
                )

                return
            }

            if (this.isClosed) {
                this.$message.warning(
                    'El pedido ya está cerrado'
                )

                return
            }

            this.form = {
                item_id: item.id,

                item_unit_type_id:
                    item.item_unit_type_id || null,

                quantity: 1,
                note: null,
            }

            this.addItem()
        },
        addItem() {
            if (!this.form.item_id) {
                this.$message.warning('Selecciona un producto')
                return
            }

            this.$http.post(`/${this.resource}/${this.orderId}/items`, this.form).then(response => {
                this.$message.success(response.data.message)
                this.form = {
                    item_id: null,
                    item_unit_type_id: null,
                    quantity: 1,
                    note: null,
                }
                this.getRecord()
            })
        },
        increaseItem(row) {
            this.form = {
                item_id: row.item_id,

                item_unit_type_id:
                    row.item_unit_type_id || null,

                quantity: 1,
                note: row.note,
            }

            this.addItem()
        },
        decreaseItem(row) {
            if (Number(row.quantity) <= 1) {
                this.deleteItem(row.id)
                return
            }

            this.$message.warning('Para reducir cantidades exactas luego hacemos edición de item')
        },
        requestDeleteItem(row) {
            if (!this.canDeleteItems) {
                this.$message.warning('No tienes permiso para eliminar productos')
                return
            }

            if (this.mustAskAuthorizationCode || ['sent', 'ready'].includes(row.status)) {
                this.authorizationForm = {
                    code: null,
                    item_id: row.id,
                }

                this.showAuthorizationDialog = true
                return
            }

            this.deleteItem(row.id)
        },

        closeAuthorizationDialog() {
            this.showAuthorizationDialog = false
            this.authorizationForm = {
                code: null,
                item_id: null,
                action: null,
            }
        },

        deleteItem(itemId, authorizationCode = null) {
            if (!this.canDeleteItems) {
                this.$message.warning('No tienes permiso para eliminar productos')
                return
            }

            this.$http.delete(`/${this.resource}/${this.orderId}/items/${itemId}`, {
                data: {
                    authorization_code: authorizationCode,
                }
            }).then(response => {
                if (response.data.success) {
                    this.$message.success(response.data.message)
                    this.closeAuthorizationDialog()
                    this.getRecord()

                    if (this.showOrderItemDetailDialog) {
                        this.orderItemDetailSelected = null
                        this.showOrderItemDetailDialog = false
                    }
                } else {
                    this.$message.warning(response.data.message)
                }
            }).catch(error => {
                this.$message.error(
                    error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : 'No se pudo eliminar el producto'
                )
            })
        },
        sendKitchen() {
            if (!this.canSendCommand) {
                this.$message.warning('No tienes permiso para generar comanda')
                return
            }

            this.$http.post(`/${this.resource}/${this.orderId}/send-kitchen`).then(response => {
                if (response.data.success) {
                    this.$message.success(response.data.message)

                    response.data.tickets.forEach(ticketId => {
                        window.open(`/${this.resource}/${this.orderId}/command/${ticketId}`, '_blank')
                    })

                    this.getRecord()
                } else {
                    this.$message.warning(response.data.message)
                }
            })
        },
        printPreAccount() {
            if (!this.canPrintPreAccount) {
                this.$message.warning('No tienes permiso para imprimir pre cuenta')
                return
            }

            const printWindow = window.open('', '_blank')

            this.$http.post(`/${this.resource}/${this.orderId}/mark-pre-account`).then(response => {
                if (response.data.success) {
                    this.$message.success(response.data.message)
                    this.getRecord()

                    if (printWindow) {
                        printWindow.location.href = `/${this.resource}/${this.orderId}/pre-account`
                    } else {
                        window.open(`/${this.resource}/${this.orderId}/pre-account`, '_blank')
                    }
                } else {
                    if (printWindow) {
                        printWindow.close()
                    }

                    this.$message.warning(response.data.message)
                }
            }).catch(error => {
                if (printWindow) {
                    printWindow.close()
                }

                this.$message.error(
                    error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : 'No se pudo solicitar la pre cuenta.'
                )
            })
        },
        openCheckout() {
            if (!this.canCharge) {
                this.$message.warning('No tienes permiso para cobrar')
                return
            }

            if (!this.canChargeByWorkflow) {
                this.$message.warning('Primero debes imprimir la pre cuenta para poder cobrar.')
                return
            }

            this.showCheckout = true
        },
        afterGenerated(payload) {
            this.generatedRecordId = payload.id
            this.generatedResource = payload.resource
            this.showGeneratedOptions = true
            this.getRecord()
        },
        goToTablesMap() {
            window.location.href = '/restaurant/v2/tables-map'
        },
        loadGeneratedRecord() {
            this.$http.get(`/${this.generatedResource}/record/${this.generatedRecordId}`).then(response => {
                this.generatedOptionsForm = response.data.data
            })
        },

        openGeneratedPrint(format) {
            const urls = {
                a4: this.generatedOptionsForm.print_a4,
                a5: this.generatedOptionsForm.print_a5,
                ticket: this.generatedOptionsForm.print_ticket,
                ticket_50: this.generatedOptionsForm.print_ticket_50,
                ticket_58: this.generatedOptionsForm.print_ticket_58,
            }

            if (urls[format]) {
                window.open(urls[format], '_blank')
            }
        },

        sendGeneratedEmail() {
            if (!this.generatedOptionsForm.customer_email) {
                this.$message.warning('Ingrese el correo')
                return
            }

            this.$http.post(`/${this.generatedResource}/email`, {
                id: this.generatedOptionsForm.id,
                customer_email: this.generatedOptionsForm.customer_email,
            }).then(response => {
                if (response.data.success) {
                    this.$message.success('Correo enviado correctamente')
                } else {
                    this.$message.warning(response.data.message || 'No se pudo enviar el correo')
                }
            })
        },

        sendGeneratedWhatsapp() {
            if (!this.generatedOptionsForm.customer_telephone) {
                this.$message.warning('Ingrese el celular')
                return
            }

            const url = this.generatedOptionsForm.print_a4 || ''
            const text = encodeURIComponent(`Comprobante ${this.generatedOptionsForm.number}: ${url}`)

            window.open(`https://wa.me/51${this.generatedOptionsForm.customer_telephone}?text=${text}`, '_blank')
        },

        closeGeneratedOptions() {
            this.showGeneratedOptions = false
            this.goToTablesMap()
        },
        openTableOptions() {
            this.moveTableForm.restaurant_dining_table_id = null
            this.showTableOptions = true
            this.getAvailableTables()
        },

        getAvailableTables() {
            this.$http.get(`/${this.resource}/${this.orderId}/available-tables`).then(response => {
                this.availableTables = response.data.data
            })
        },

        moveTable() {
            this.$http.post(`/${this.resource}/${this.orderId}/move-table`, this.moveTableForm).then(response => {
                if (response.data.success) {
                    this.$message.success(response.data.message)
                    this.showTableOptions = false
                    this.getRecord()
                } else {
                    this.$message.warning(response.data.message)
                }
            })
        },

        cancelOrder(authorizationCode = null) {
            this.$confirm('¿Seguro que deseas cancelar el pedido y liberar la mesa?', 'Confirmar', {
                confirmButtonText: 'Sí, liberar',
                cancelButtonText: 'No',
                type: 'warning',
            }).then(() => {
                this.$http.post(`/${this.resource}/${this.orderId}/cancel`, {
                    authorization_code: authorizationCode,
                }).then(response => {
                    if (response.data.success) {
                        this.$message.success(response.data.message)
                        this.closeAuthorizationDialog()
                        this.showTableOptions = false
                        this.getRecord()
                    } else {
                        this.$message.warning(response.data.message)
                    }
                }).catch(error => {
                    this.$message.error(
                        error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'No se pudo liberar la mesa'
                    )
                })
            })
        },

        orderItemStatusText(status) {
            const labels = {
                pending: 'Pendiente',
                sent: 'Enviado',
                ready: 'Preparado',
            }

            return labels[status] || status
        },

        orderItemStatusType(status) {
            const types = {
                pending: 'warning',
                sent: 'success',
                ready: 'primary',
            }

            return types[status] || 'info'
        },

        requestCancelOrder() {
            if (this.mustAskAuthorizationCode) {
                this.authorizationForm = {
                    code: null,
                    item_id: null,
                    action: 'cancel_order',
                }

                this.showAuthorizationDialog = true
                return
            }

            this.cancelOrder()
        },

        confirmAuthorizedDelete() {
            if (!this.authorizationForm.code) {
                this.$message.warning('Ingrese el código de autorización')
                return
            }

            if (this.authorizationForm.action === 'cancel_order') {
                this.cancelOrder(this.authorizationForm.code)
                return
            }

            this.deleteItem(this.authorizationForm.item_id, this.authorizationForm.code)
        },

        canOpenOrderItemDetail(row) {
            return !!row && !!row.id
        },

        openOrderItemDetail(row) {
            this.orderItemDetailSelected = row
            this.showOrderItemDetailDialog = true
        },

        formatOrderItemDate(value) {
            if (!value) {
                return '-'
            }

            return moment(value).format('DD-MM-YYYY h:mm:ss A')
        },

        openOrderItemNoteDialog(row) {
            this.noteOrderItemSelected = row

            const currentNote = row.note || ''

            this.noteProductForm = {
                quantity: 1,
                tags: [],
                custom_note: currentNote,
            }

            this.showNoteProductDialog = true
        },

        submitOrderItemNote() {
            if (!this.noteOrderItemSelected) {
                return
            }

            const note = this.buildProductNote()

            this.$http.put(`/${this.resource}/${this.orderId}/items/${this.noteOrderItemSelected.id}/note`, {
                note: note,
            }).then(response => {
                if (response.data.success) {
                    this.$message.success(response.data.message)
                    this.closeNoteProductDialog()
                    this.getRecord()
                } else {
                    this.$message.warning(response.data.message)
                }
            })
        },
        orderItemDescription(row) {
            if (!row) {
                return ''
            }

            const productDescription =
                row.item && row.item.description
                    ? row.item.description
                    : 'Producto'

            const presentationDescription =
                row.presentation
                    && row.presentation.description
                    ? row.presentation.description
                    : null

            if (!presentationDescription) {
                return productDescription
            }

            return `${productDescription} - ${presentationDescription}`
        },
    }
}
</script>

<style>
.restaurant-page-header {
    margin-bottom: 0;
}

.restaurant-pos {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 390px;
    gap: 16px;
    padding: 5px;
    min-height: calc(100vh - 120px);
}

.restaurant-pos-main {
    min-width: 0;
}

.restaurant-table-summary {
    background: #ffffff;
    border: 1px solid #e9edf2;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.restaurant-table-summary h4 {
    margin: 0 0 4px;
    color: #374151;
    font-size: 18px;
    font-weight: 700;
}

.restaurant-table-summary p {
    margin: 0;
    color: #6b7280;
}

.restaurant-table-summary strong {
    color: #374151;
    font-size: 26px;
    font-weight: 800;
}

.restaurant-category-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    overflow-x: auto;
    padding-bottom: 2px;
}

.restaurant-category-tabs button {
    border: 1px solid #d6dde6;
    background: #ffffff;
    color: #374151;
    border-radius: 8px;
    padding: 9px 16px;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.restaurant-category-tabs button.active {
    background: #12a79c;
    border-color: #12a79c;
    color: #ffffff;
}

.restaurant-products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px;
    max-height: calc(100vh - 250px);
    overflow-y: auto;
    padding-right: 4px;
}

.restaurant-product-card {
    background: #ffffff;
    border: 1px solid #e9edf2;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.15s ease;
}

.restaurant-product-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.restaurant-product-image {
    height: 115px;
    background: #f8fafc;
    border-bottom: 1px solid #eef2f6;
}

.restaurant-product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.restaurant-product-placeholder {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a5afbd;
    font-size: 34px;
}

.restaurant-product-body {
    padding: 10px;
}

.restaurant-product-body h5 {
    height: 34px;
    overflow: hidden;
    margin: 0 0 8px;
    color: #1f2937;
    font-size: 13px;
    font-weight: 800;
    line-height: 1.25;
}

.restaurant-product-presentation {
    min-height: 20px;
    margin-bottom: 7px;
    color: #0f766e;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
}

.restaurant-product-presentation.empty {
    visibility: hidden;
}

.restaurant-product-card.has-presentation {
    border-color: rgba(18, 167, 156, 0.35);
}

.restaurant-product-card.has-presentation:hover {
    border-color: #12a79c;
}

.restaurant-product-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.restaurant-product-footer strong {
    color: #12a79c;
    font-size: 14px;
    font-weight: 900;
}

.btn-add-product {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    border: 0;
    background: #12a79c;
    color: #ffffff;
}

.restaurant-order-panel {
    min-width: 0;
}

.restaurant-order-card {
    height: calc(100vh - 150px);
    background: #ffffff;
    border: 1px solid #e9edf2;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.restaurant-order-header {
    padding: 16px;
    border-bottom: 1px solid #eef2f6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.restaurant-order-header h3 {
    margin: 0;
    color: #1f2937;
    font-size: 20px;
    font-weight: 800;
}

.restaurant-order-header span {
    color: #7b8494;
    font-size: 12px;
}

.restaurant-order-header strong {
    color: #374151;
    font-size: 20px;
    font-weight: 900;
}

.restaurant-order-status {
    margin-top: 6px;
}

.restaurant-order-actions strong {
    display: block;
    color: #374151;
    font-size: 13px;
    margin-bottom: 6px;
}

.restaurant-order-totals {
    padding: 14px 16px;
    background: #f8fafc;
    border-top: 1px solid #eef2f6;
}

.restaurant-order-totals>div {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    color: #5b6676;
}

.restaurant-order-totals strong {
    color: #374151;
}

.restaurant-order-totals .grand-total {
    margin-top: 8px;
    padding-top: 10px;
    border-top: 1px dashed #d6dde6;
    font-size: 20px;
    font-weight: 900;
}

.restaurant-order-buttons {
    padding: 12px 16px;
    border-top: 1px solid #eef2f6;
}

.restaurant-order-buttons.compact {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.restaurant-order-buttons.compact .btn {
    min-height: 44px;
    border-radius: 8px;
    font-weight: 800;
    font-size: 13px;
    padding: 8px 6px;
    line-height: 1.1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.restaurant-order-buttons.compact .btn i {
    font-size: 14px;
}

.restaurant-order-buttons.compact .btn span {
    white-space: nowrap;
}

.restaurant-empty-products,
.restaurant-empty-order {
    padding: 24px;
    color: #7b8494;
    text-align: center;
    background: #ffffff;
    border: 1px dashed #d6dde6;
    border-radius: 8px;
}

@media (max-width: 991px) {
    .restaurant-pos {
        grid-template-columns: 1fr;
    }

    .restaurant-order-card {
        height: auto;
        min-height: 520px;
    }

    .restaurant-products-grid {
        max-height: none;
    }
}

.restaurant-order-items {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 6px 16px;
}

.restaurant-order-panel {
    min-width: 0;
}

.restaurant-order-card {
    height: calc(100vh - 132px);
    background: #ffffff;
    border: 1px solid #e9edf2;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.restaurant-order-header.compact {
    padding: 12px 16px;
    border-bottom: 1px solid #eef2f6;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
}

.restaurant-order-header.compact h3 {
    margin: 0;
    color: #1f2937;
    font-size: 18px;
    font-weight: 800;
}

.restaurant-order-header.compact span {
    display: block;
    margin-top: 2px;
    color: #7b8494;
    font-size: 12px;
}

.restaurant-order-header.compact>strong {
    color: #1f2937;
    font-size: 20px;
    font-weight: 900;
    white-space: nowrap;
}

.restaurant-order-items {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 6px 14px;
}

.restaurant-order-row {
    padding: 9px 0;
    border-bottom: 1px solid #eef2f6;
}

.restaurant-order-row-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 5px;
}

.restaurant-order-product-title {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.restaurant-order-product-title strong {
    max-width: 155px;
    white-space: normal;
    overflow-wrap: break-word;
    color: #1f2937;
    font-size: 13px;
    font-weight: 900;
    line-height: 1.25;
}

.restaurant-row-total {
    color: #1f2937;
    font-size: 16px;
    font-weight: 900;
    white-space: nowrap;
}

.restaurant-order-row-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.restaurant-row-detail {
    min-width: 0;
    max-width: 185px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: #5b6676;
    font-size: 12px;
}

.restaurant-row-actions {
    display: inline-flex;
    align-items: center;
    border: 1px solid #d6dde6;
    border-radius: 7px;
    overflow: hidden;
    flex-shrink: 0;
}

.restaurant-row-actions button {
    width: 25px;
    height: 25px;
    border: 0;
    background: #f8fafc;
    color: #374151;
    font-weight: 800;
    line-height: 1;
}

.restaurant-row-actions span {
    width: 28px;
    height: 25px;
    line-height: 25px;
    background: #ffffff;
    text-align: center;
    font-weight: 800;
}

.restaurant-row-actions .delete {
    width: 30px;
    background: #dc3545;
    color: #ffffff;
    border-left: 1px solid #d6dde6;
}

.restaurant-order-footer {
    flex-shrink: 0;
    background: #f8fafc;
    border-top: 1px solid #eef2f6;
}

.restaurant-total-line {
    padding: 14px 16px;
    border-bottom: 1px dashed #d6dde6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.restaurant-total-line span {
    color: #5b6676;
    font-size: 20px;
    font-weight: 800;
}

.restaurant-total-line strong {
    color: #1f2937;
    font-size: 24px;
    font-weight: 900;
}

.restaurant-order-buttons {
    padding: 12px 16px;
}

.restaurant-order-buttons.compact {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.restaurant-order-buttons.compact .btn {
    min-height: 42px;
    border-radius: 8px;
    font-weight: 800;
    font-size: 12px;
    padding: 7px 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    line-height: 1.1;
}

.restaurant-order-buttons.compact .btn span {
    white-space: nowrap;
}

.restaurant-closed-alert {
    padding: 12px 16px;
    background: #eef8f7;
    color: #007575;
    font-weight: 800;
    text-align: center;
    border-top: 1px solid #d6eeee;
}

.restaurant-closed-state {
    min-height: calc(100vh - 270px);
    background: #ffffff;
    border: 1px solid #e9edf2;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #374151;
    text-align: center;
    padding: 30px;
}

.restaurant-closed-state i {
    font-size: 54px;
    color: #12a79c;
    margin-bottom: 14px;
}

.restaurant-closed-state h3 {
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 8px;
}

.restaurant-closed-state p {
    color: #7b8494;
    margin-bottom: 18px;
}

.restaurant-closed-state .btn {
    min-height: 40px;
    border-radius: 8px;
    font-weight: 800;
}

.restaurant-generated-dialog .el-dialog__header {
    padding: 22px 24px 12px;
    border-bottom: 1px solid #eef2f6;
}

.restaurant-generated-dialog .el-dialog__title {
    font-size: 20px;
    font-weight: 800;
    color: #333;
}

.restaurant-generated-dialog .el-dialog__body {
    padding: 18px 22px 24px;
}

.restaurant-print-options {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 18px;
    margin: 28px 0;
}

.restaurant-print-option {
    text-align: center;
    cursor: pointer;
    font-weight: 800;
    color: #777;
}

.restaurant-print-option button {
    width: 46px;
    height: 46px;
    border: 0;
    border-radius: 6px;
    background: #26b9e8;
    color: #fff;
    font-size: 18px;
    box-shadow: 0 2px 5px rgba(38, 185, 232, 0.35);
}

.restaurant-print-option span {
    display: block;
    margin-top: 8px;
}

.restaurant-ready-alert {
    margin: 12px 14px 0;
    padding: 10px 12px;
    border-radius: 8px;
    background: #eef6ff;
    border: 1px solid #b8dcff;
    color: #2378d4;
    font-size: 14px;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 8px;
}

.restaurant-ready-alert i {
    color: #409eff;
}

.restaurant-product-card.without-image {
    min-height: 130px;
}

.restaurant-product-card.without-image .restaurant-product-body {
    min-height: 130px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.restaurant-disabled-action {
    opacity: .55;
    filter: grayscale(.15);
}

.restaurant-preaccount-required-alert {
    margin: 10px 0 0;
    padding: 8px 10px;
    border-radius: 6px;
    background: #fff8e6;
    color: #b7791f;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    border: 1px solid #ffe0a3;
}

.restaurant-preaccount-required-alert i {
    margin-right: 5px;
}

.restaurant-order-buttons.compact button span {
    white-space: nowrap;
}

.restaurant-product-actions {
    display: flex;
    gap: 6px;
    align-items: center;
}

.btn-note-product {
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    background: #f59e0b;
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
}

.btn-note-product:hover {
    background: #d88900;
}

.restaurant-note-product-header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    margin-bottom: 16px;
    border: 1px solid #e5e9f2;
    border-radius: 8px;
    background: #f8fafc;
}

.restaurant-note-product-header strong {
    color: #1f2937;
}

.restaurant-note-product-header span {
    color: #00a99d;
    font-weight: 800;
}

.restaurant-note-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.restaurant-note-tags button {
    border: 1px solid #dbe3ee;
    border-radius: 16px;
    padding: 5px 10px;
    background: #fff;
    color: #4b5563;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.restaurant-note-tags button.active {
    border-color: #00a99d;
    background: #e6fffb;
    color: #008f86;
}

.restaurant-row-detail-wrap {
    min-width: 0;
    max-width: 190px;
}

.restaurant-note-bubble {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 185px;
    margin-top: 5px;
    padding: 4px 8px;
    border-radius: 14px;
    background: #fff4c2;
    border: 1px solid #facc15;
    color: #8a5a00;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.2;
    white-space: normal;
}

.restaurant-note-bubble i {
    color: #d88900;
}

.restaurant-order-section-title {
    margin: 10px 0 6px;
    padding: 6px 9px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
}

.restaurant-order-section-title.pending {
    background: #fff8e6;
    color: #b7791f;
}

.restaurant-order-section-title.sent {
    background: #eefaf6;
    color: #07856f;
}

.restaurant-order-row.pending-row {
    background: #fffdf6;
}

.restaurant-order-row.sent-row {
    opacity: .82;
}

.restaurant-row-side {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.restaurant-row-detail-button {
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
    text-align: left;
}

.restaurant-row-detail-button:hover {
    color: #00a99d;
    text-decoration: underline;
}

.restaurant-order-item-detail-dialog .el-dialog__header {
    padding: 18px 22px 12px;
    border-bottom: 1px solid #eef2f6;
}

.restaurant-detail-title span,
.restaurant-detail-title strong {
    display: block;
}

.restaurant-detail-title span {
    color: #4b5563;
    font-size: 20px;
    font-weight: 500;
}

.restaurant-detail-title strong {
    margin-top: 3px;
    color: #4b5563;
    font-size: 20px;
    font-weight: 700;
}

.restaurant-order-item-detail-row {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    padding: 12px 0;
    border-bottom: 1px solid #eef2f6;
}

.restaurant-order-item-detail-row:last-child {
    border-bottom: 0;
}

.restaurant-order-item-detail-info {
    min-width: 0;
}

.restaurant-order-item-detail-date {
    color: #4b6475;
    font-size: 15px;
    margin-bottom: 6px;
}

.restaurant-order-item-detail-date i {
    margin-right: 5px;
}

.restaurant-order-item-detail-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #9aa8b5;
    font-size: 15px;
}

.restaurant-order-item-detail-note {
    margin-top: 8px;
    /* display: flex; */
    align-items: center;
    gap: 8px;
    color: #9aa8b5;
    font-size: 15px;
}

.restaurant-order-item-detail-note strong {
    display: inline-block;
    max-width: 360px;
    padding: 4px 10px;
    border-radius: 6px;
    background: #00a8e8;
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
}

.restaurant-order-item-detail-delete {
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 6px;
    background: #ff3860;
    color: #fff;
    flex-shrink: 0;
}
</style>



// WEBPACK FOOTER //
// modules/Restaurant/Resources/assets/js/views/v2/orders/show.vue